<script setup lang="ts">
// 회사 지도 Vue 래퍼. 엔진(lib/relmap)에 vault 데이터를 먹이고, 데이터가 바뀌면 다시 그린다.
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useVaultStore } from '../../stores/vault'
import { layoutOrg, companyColor } from '../../lib/layoutOrg'
import { createRelMap, type RmApi, type RmData } from '../../lib/relmap'

const props = defineProps<{ mode: 'rel' | 'org'; calm: boolean }>()
const emit = defineEmits<{ select: [id: string | null]; me: [] }>()

const vault = useVaultStore()
const elRef = ref<HTMLElement | null>(null)
let api: RmApi | null = null

function buildData(): RmData {
  const me = { dept: null as string | null, level: 4 } // '나' 조직 정보는 지도 이후 증분
  const lo = layoutOrg(vault.people, vault.depts, me, vault.companies)
  const compName = (id?: string | null) => vault.companies.find(c => c.id === id)?.name
  return {
    people: vault.people.map(p => ({
      id: p.id, name: p.name, title: p.title, rel: p.rel, close: p.close,
      c1: p.c1, c2: p.c2, org: lo.orgOf[p.id] || [0, 0], boss: p.boss,
      company: p.company || undefined, companyName: compName(p.company), companyColor: companyColor(vault.companies, p.company),
    })),
    depts: lo.deptRects,
    links: vault.relations.map(r => ({ a: r.a, b: r.b, type: r.type, note: r.note })),
    me: { count: vault.entries.length, org: lo.meOrg, boss: undefined },
  }
}

function build() {
  if (!elRef.value) return
  api?.destroy()
  api = createRelMap(elRef.value, buildData(), {
    onSelect: (node) => emit('select', node ? node.id : null),
    onMe: () => emit('me'),
  })
  api.setMode(props.mode)
  api.setCalm(props.calm)
}

// 사람·부서·관계가 바뀌면(관리 화면 편집 등) 다시 그린다. 기록 수(마스코트 크기)는 grow()로 따로 반영.
const sig = computed(() => JSON.stringify([
  vault.people.map(p => [p.id, p.close, p.dept, p.level, p.boss, p.c1, p.company]),
  vault.depts.map(d => [d.id, d.name]),
  vault.companies.map(c => [c.id, c.name]),
  vault.relations.map(r => [r.id, r.a, r.b, r.type]),
]))
watch(sig, build)
watch(() => props.mode, m => api?.setMode(m))
watch(() => props.calm, c => api?.setCalm(c))

onMounted(build)
onUnmounted(() => api?.destroy())

defineExpose({
  grow: () => api?.grow(),
  ping: (id: string) => api?.ping(id),
})
</script>

<template>
  <div ref="elRef"></div>
</template>
